import { useCallback, useEffect, useMemo, useState } from "react";
import { useAdmin } from "../admin";
import { ROLES, roleName } from "../lib/scenario";
import { errorText, supabase } from "../supabase";
import { ConfirmButton } from "./Library";

// The roster: import it from a CSV (spec section 10), reset a forgotten PIN (plan
// decision: the training lead does this in the web admin), and mark someone who has
// left. The roster only says who works where; it holds no scores.

interface Person {
  id: string;
  name: string;
  phone: string;
  role: string;
  site_id: string;
  reports_to: string | null;
  shift: string | null;
  active: boolean;
  auth_user_id: string | null;
}

interface ImportReply {
  ok?: boolean;
  imported?: boolean;
  errors?: { line: number; field: string | null; message: string }[];
  created?: number;
  updated?: number;
  error?: string;
}

const TEMPLATE =
  "name,phone,role,site,reports to phone,shift\n" +
  "Example Guard,9876543210,guard,Your Site Name,9876543200,night\n";

export default function People() {
  const { sites } = useAdmin();
  const [people, setPeople] = useState<Person[] | null>(null);
  const [error, setError] = useState("");
  const [site, setSite] = useState("");
  const [role, setRole] = useState("");
  const [search, setSearch] = useState("");
  const [showLeft, setShowLeft] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const load = useCallback(() => {
    supabase
      .from("staff")
      .select("id, name, phone, role, site_id, reports_to, shift, active, auth_user_id")
      .order("name")
      .then(({ data, error }) => (error ? setError(errorText(error)) : setPeople(data as Person[])));
  }, []);
  useEffect(load, [load]);

  const byId = useMemo(() => new Map((people ?? []).map((p) => [p.id, p])), [people]);
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? "";
  const shown = (people ?? []).filter((p) => {
    if (!showLeft && !p.active) return false;
    if (site && p.site_id !== site) return false;
    if (role && p.role !== role) return false;
    const words = search.trim().toLowerCase();
    return !words || p.name.toLowerCase().includes(words) || p.phone.includes(words);
  });

  async function resetPin(p: Person) {
    const { error } = await supabase.rpc("reset_pin", { p_staff: p.id });
    setNotice(
      error
        ? { kind: "error", text: errorText(error) }
        : { kind: "ok", text: `${p.name}'s PIN is cleared. They set a new one the next time they sign in with a network.` },
    );
  }

  async function setActive(p: Person, active: boolean) {
    const { error } = await supabase.rpc("admin_set_staff_active", { p_staff: p.id, p_active: active });
    if (error) setNotice({ kind: "error", text: errorText(error) });
    else {
      setNotice({
        kind: "ok",
        text: active ? `${p.name} is active again.` : `${p.name} is marked as left. They no longer get practice or can sign in.`,
      });
      load();
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>People</h1>
      </div>
      <RosterImport onImported={load} />

      <div className="filters" style={{ marginTop: 24 }}>
        <input type="search" placeholder="Search name or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Site" value={site} onChange={(e) => setSite(e.target.value)}>
          <option value="">All sites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {roleName(r)}
            </option>
          ))}
        </select>
        <label className="check">
          <input type="checkbox" checked={showLeft} onChange={(e) => setShowLeft(e.target.checked)} /> Show people who
          have left
        </label>
      </div>
      {notice && (
        <div className={`notice ${notice.kind}`} style={{ marginBottom: 12 }}>
          {notice.text}
        </div>
      )}
      {error && <div className="notice error">{error}</div>}
      <div className="card table-card">
        {!people && !error && <p className="empty">Loading…</p>}
        {people && shown.length === 0 && (
          <p className="empty">{people.length === 0 ? "No one on the roster yet. Import a CSV above." : "No one matches."}</p>
        )}
        {shown.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Site</th>
                <th>Reports to</th>
                <th>Shift</th>
                <th>Signed in</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} style={p.active ? undefined : { opacity: 0.6 }}>
                  <td>
                    <strong>{p.name}</strong>
                    <div className="small muted">{p.phone}</div>
                  </td>
                  <td className="small">{roleName(p.role)}</td>
                  <td className="small">{siteName(p.site_id)}</td>
                  <td className="small">{p.reports_to ? byId.get(p.reports_to)?.name ?? "" : "–"}</td>
                  <td className="small">{p.shift ?? "–"}</td>
                  <td className="small">{p.auth_user_id ? "Yes" : "Not yet"}</td>
                  <td>
                    <div className="row" style={{ justifyContent: "flex-end", flexWrap: "nowrap" }}>
                      {p.active ? (
                        <>
                          <ConfirmButton
                            label="Reset PIN"
                            quiet
                            confirm={`Clear ${p.name}'s PIN?`}
                            onConfirm={() => resetPin(p)}
                          />
                          <ConfirmButton
                            label="Mark as left"
                            confirm={`${p.name} has left?`}
                            onConfirm={() => setActive(p, false)}
                          />
                        </>
                      ) : (
                        <button className="btn quiet small" onClick={() => setActive(p, true)}>
                          Make active
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function RosterImport({ onImported }: { onImported: () => void }) {
  const { sites } = useAdmin();
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState<(ImportReply & { dryRun: boolean }) | null>(null);

  async function send(dryRun: boolean) {
    if (!file) return;
    setBusy(true);
    setReply(null);
    const { data, error } = await supabase.functions.invoke("import-roster", { body: { csv: file.text, dryRun } });
    setBusy(false);
    if (error) {
      let text = errorText(error);
      try {
        const body = await (error as { context?: Response }).context?.json();
        if (body?.error) text = body.error;
      } catch {
        // keep the general message
      }
      setReply({ error: text, dryRun });
      return;
    }
    setReply({ ...(data as ImportReply), dryRun });
    if ((data as ImportReply).imported) onImported();
  }

  return (
    <section className="card stack">
      <h2>Import the roster</h2>
      <p className="muted">
        A CSV with the columns <code>name, phone, role, site, reports to phone, shift</code>. Roles are guard,
        housekeeping, guard-supervisor, housekeeping-supervisor and facility-manager. Sites must already be in Settings
        {sites.length ? ` (${sites.map((s) => s.name).join(", ")})` : ""}. Nothing is imported unless every row is right.
        People already on the roster are updated by phone number; people missing from the file are left as they are.
      </p>
      <div className="row">
        <label className="btn quiet">
          {file ? "Choose another file" : "Choose CSV file"}
          <input
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              setReply(null);
              if (f) setFile({ name: f.name, text: await f.text() });
            }}
          />
        </label>
        {file && <span>{file.name}</span>}
        <button className="btn outline" disabled={!file || busy} onClick={() => send(true)}>
          Check file
        </button>
        <button className="btn" disabled={!file || busy} onClick={() => send(false)}>
          Import
        </button>
        <a href={"data:text/csv;charset=utf-8," + encodeURIComponent(TEMPLATE)} download="roster-template.csv" className="small">
          Download a template
        </a>
      </div>
      {busy && <p className="muted">Working…</p>}
      {reply?.error && <div className="notice error">{reply.error}</div>}
      {reply && !reply.error && reply.errors && reply.errors.length > 0 && (
        <div className="notice error">
          <strong>Nothing was imported. Fix these rows and try again:</strong>
          <table style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>Line</th>
                <th>Column</th>
                <th>Problem</th>
              </tr>
            </thead>
            <tbody>
              {reply.errors.map((e, i) => (
                <tr key={i}>
                  <td>{e.line || "–"}</td>
                  <td>{e.field ?? "–"}</td>
                  <td>{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {reply && !reply.error && reply.ok && (
        <div className="notice ok">
          {reply.dryRun
            ? `The file looks right: ${reply.created} new and ${reply.updated} updated. Press Import to bring it in.`
            : `Imported: ${reply.created} new and ${reply.updated} updated. New people get practice from tonight.`}
        </div>
      )}
    </section>
  );
}
